// Configurable JWT claim resolver. Instead of hardcoding where user ID, name,
// email, roles, and tenant live in the JWT payload (different for Keycloak,
// Auth0, Azure AD, tc-identity, etc.), the deployer declares dot-path mappings
// in the product line config. This resolver extracts values at runtime.

export interface ClaimMapping {
  /** Path to user ID (default: 'sub'). */
  userId?: string;
  /** Path to display name (default: 'name'). */
  name?: string;
  /** Fallback path for name (default: 'preferred_username'). */
  nameFallback?: string;
  /** Path to email (default: 'email'). */
  email?: string;
  /** Path to roles array (default: 'roles'). */
  roles?: string;
  /** Path to tenant ID (default: 'tenant'). */
  tenant?: string;
}

export const DEFAULT_CLAIM_MAPPING: Required<ClaimMapping> = {
  userId: 'sub',
  name: 'name',
  nameFallback: 'preferred_username',
  email: 'email',
  roles: 'roles',
  tenant: 'tenant',
};

/** Resolve a dot-path (e.g. 'realm_access.roles') against a JWT payload. */
export function resolveClaim(payload: Record<string, unknown>, path: string): unknown {
  let current: unknown = payload;
  for (const segment of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

export interface ResolvedIdentity {
  userId: string;
  name: string;
  email: string;
  roles: string[];
  tenant: string;
}

export function resolveIdentity(
  payload: Record<string, unknown>,
  mapping: ClaimMapping = {},
  fallbackTenant?: string
): ResolvedIdentity {
  const m = { ...DEFAULT_CLAIM_MAPPING, ...mapping };
  const userId = String(resolveClaim(payload, m.userId) ?? '');
  const name =
    String(resolveClaim(payload, m.name) ?? '') ||
    String(resolveClaim(payload, m.nameFallback) ?? '') ||
    'Unknown';
  const email = String(resolveClaim(payload, m.email) ?? '');

  const rawRoles = resolveClaim(payload, m.roles);
  // Array claim (preferred) or single string (e.g. tc-identity `role_name`).
  const roles = Array.isArray(rawRoles)
    ? rawRoles.map(String)
    : rawRoles != null && String(rawRoles).trim() !== ''
      ? [String(rawRoles)]
      : [];

  const tenant = String(resolveClaim(payload, m.tenant) ?? fallbackTenant ?? '');

  return { userId, name, email, roles, tenant };
}
